import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, logError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) { setState("failed"); return; }
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(r.data.payment_status)) { setState("failed"); return; }
      } catch (e) { logError("payment status poll failed", e); }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-6">
      <div className="text-center max-w-md" data-testid="payment-success-page">
        {state === "checking" && (
          <><Loader2 className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
          <h1 className="font-display text-2xl font-semibold">Confirming your payment...</h1>
          <p className="text-muted-foreground mt-2">Please wait a moment.</p></>
        )}
        {state === "paid" && (
          <><CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="font-display text-3xl font-bold" data-testid="payment-confirmed">Booking confirmed! 🎉</h1>
          <p className="text-muted-foreground mt-2">Your experience is booked. Check your trips for details and directions.</p>
          <div className="flex gap-3 justify-center mt-6">
            <Button data-testid="go-dashboard" onClick={() => navigate("/dashboard")} className="rounded-full">View my trips</Button>
            <Link to="/activities"><Button variant="outline" className="rounded-full">Explore more</Button></Link>
          </div></>
        )}
        {state === "failed" && (
          <><XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-semibold">Payment not confirmed</h1>
          <p className="text-muted-foreground mt-2">We couldn't confirm your payment. If you were charged, check your trips or contact support.</p>
          <Button onClick={() => navigate("/dashboard")} className="rounded-full mt-6">Go to my trips</Button></>
        )}
      </div>
    </div>
  );
}
